/**
 * Descreve a tela hospedeira combinando o contexto publicado pelos módulos
 * com campos, regiões e ações visíveis no DOM. A leitura ocorre ao enviar o
 * turno e exclui o próprio Assistente. O envio dos dados depende do
 * consentimento da pessoa. O snapshot não comprova que dados foram salvos.
 */
import { JanelaDaTela, lerRotaDaJanela } from './navegacaoDaTela'
import {
  LIMITE_DE_ACOES,
  LIMITE_DE_CAMPOS_DE_FORMULARIO,
  LIMITE_DE_ESTADOS,
  LIMITE_DE_CARACTERES_DO_SNAPSHOT,
  LIMITE_DE_ITENS_DO_SNAPSHOT,
  LIMITE_DE_TEXTO_DO_ITEM,
  RegiaoComLinhas,
  preencherOrcamentoDoSnapshot
} from './orcamentoDeItensDeTela'
import {
  ContextoPublicadoPelaTela,
  ItemPublicadoPelaTela,
  colherContextoPublicadoPelaTela
} from './provedorDeContextoDaTela'
import { TipoDeItemDeTela, ehTipoDeDados, ehTipoDeEstrutura } from './tiposDeItemDeTela'

/** Teto de campos de formulário. Cada lista do snapshot tem teto próprio. */
export const LIMITE_DE_CAMPOS = LIMITE_DE_CAMPOS_DE_FORMULARIO

/** Teto de ações (botões, links, itens de menu) publicadas no snapshot. */
export const LIMITE_DE_ACOES_OBSERVADAS = LIMITE_DE_ACOES

/** Teto do array completo, espelhado do contrato do backend. */
export const LIMITE_DE_ITENS = LIMITE_DE_ITENS_DO_SNAPSHOT

/** Teto de caracteres de rótulo e de valor. Acima disso, trunca-se marcando. */
export const LIMITE_DE_TEXTO = LIMITE_DE_TEXTO_DO_ITEM

/** Teto local da soma de rótulos e valores. */
export const LIMITE_DE_CARACTERES = LIMITE_DE_CARACTERES_DO_SNAPSHOT

/** Controles que a pessoa preenche. Botões ficam de fora (não são campos). */
const SELETOR_DE_CAMPOS = 'input, select, textarea'

/** Regiões tabulares ou de lista que têm semântica navegável no DOM. */
const SELETOR_DE_REGIOES = 'table, [role="table"], [role="grid"], [role="list"], ul, ol'

/** Controles que a própria semântica do DOM apresenta como filtros. */
const SELETOR_DE_ESCOPO_DE_FILTROS = [
  '[role="search"]',
  '[data-filtro]',
  '[data-filter]',
  '[data-filtros]',
  '[data-filters]'
].join(', ')

/** `type`s de `<input>` que são ação, não preenchimento. */
const TIPOS_DE_ACAO = new Set(['submit', 'button', 'reset', 'image'])

/**
 * O que a pessoa pode ACIONAR na tela.
 *
 * Botão ficou de fora do snapshot desde o começo, com o argumento de que não é
 * campo de preenchimento — verdade que virou cegueira: perguntada sobre o que
 * dá para fazer na tela, a resposta não tinha de onde sair. Ação é a terceira
 * lista do contrato, e é ela que separa "descrever" de "orientar".
 */
const SELETOR_DE_ACOES = [
  'button',
  '[role="button"]',
  '[role="menuitem"]',
  '[role="link"]',
  'a[href]',
  'input[type="submit"]',
  'input[type="button"]',
  'input[type="reset"]'
].join(', ')

/** Âncoras de navegação interna que não são ação de tela. */
const HREFS_SEM_ACAO = new Set(['#', ''])

/** Tags cujo texto NÃO é rótulo (o valor do próprio controle, opções do select). */
const TAGS_SEM_TEXTO_DE_ROTULO = new Set([
  'input', 'select', 'textarea', 'button', 'option', 'optgroup', 'datalist', 'script', 'style'
])

/**
 * Radicais de campo sensível, comparados por PREFIXO DE TOKEN sobre nome, id,
 * rótulo, placeholder e autocomplete (normalizados: sem acento, minúsculos,
 * camelCase e separadores quebrados em tokens).
 *
 * Prefixo de token, e não substring solta, é o que separa peneira de parede:
 * `senha` casa com "senhaAtual" e "confirmar_senha", e NÃO casa com "desenho da
 * escala"; `rg` casa com o documento e não com "energia"/"organização".
 */
const RADICAIS_SENSIVEIS: readonly string[] = [
  // Senha e segredo
  'senha', 'password', 'passwd', 'pwd', 'secret', 'segredo',
  'token', 'apikey', 'credencial', 'credential', 'authorization',
  // Cartão de crédito (o nome inteiro; "cartão" sozinho é cartão de PONTO)
  'creditcard', 'cardnumber', 'cvv', 'cvc',
  // Documentos pessoais
  'cpf', 'rg', 'pis', 'pasep'
]

/**
 * Sensíveis que só existem como COMBINAÇÃO. É o que permite excluir "cartão de
 * crédito" e "chave de acesso" sem excluir "cartão de ponto" e "chave do
 * registro" — vocabulário corrente do produto, que precisa continuar visível.
 */
const PARES_SENSIVEIS: ReadonlyArray<readonly [string, string]> = [
  ['api', 'key'],
  ['cartao', 'credito'],
  ['cartao', 'debito'],
  ['credit', 'card'],
  ['cc', 'number'],
  ['cc', 'csc'],
  ['chave', 'acesso'],
  ['chave', 'privada'],
  ['chave', 'secreta'],
  ['chave', 'api']
]

const MARCAS_DIACRITICAS = /\p{Diacritic}/gu

/**
 * Até onde subir procurando o contêiner do campo. Quatro níveis cobrem o
 * `p-textfield` do design system e param bem antes do wrapper da tela inteira.
 */
const MAX_NIVEIS_DE_CONTAINER = 4

export type OrigemDoRotulo =
  | 'aria-labelledby'
  | 'aria-label'
  | 'label-for'
  | 'label-ancestral'
  | 'label-no-container'
  | 'texto-no-container'
  | 'placeholder'
  | 'sem_rotulo'

export interface ItemDeTela {
  /** `name`, ou `id` quando não há `name`. `null` quando a tela não dá nenhum. */
  nome: string | null
  /** Rótulo como a PESSOA o vê. `null` quando a tela não associa nenhum. */
  rotulo: string | null
  /** De onde o rótulo veio — inclusive `sem_rotulo`, que é registro, não sumiço. */
  origem_do_rotulo: OrigemDoRotulo
  tipo: string
  valor: string
  preenchido: boolean
  /** Presente só quando o rótulo foi cortado no cap. Truncar não é silencioso. */
  rotulo_truncado?: true
  /** Presente só quando o valor foi cortado no cap. Truncar não é silencioso. */
  valor_truncado?: true
  /** Só em ações: o controle está à vista, mas não está acionável agora. */
  desabilitado?: true
}

/**
 * Nome histórico do item. Continua exportado porque meia dúzia de módulos
 * (casamento de campo, preenchimento) falam de CAMPO e só de campo.
 */
export type CampoObservado = ItemDeTela

/**
 * O que o host colheu da tela aberta. NÃO-AUTORITATIVO por construção: descreve
 * a tela, jamais o banco. Ver a nota de fronteira no topo do arquivo.
 *
 * TRÊS listas, e não uma. Numa lista só, a linha "03/09 · João · 08:00 12:00"
 * chegava ao modelo com a mesma forma do campo que a pessoa está digitando, e
 * ele tratava dado exibido como rascunho de formulário. A separação é o
 * contrato: `estrutura` diz o que a tela É, `dados` o que ela MOSTRA e
 * `acoes_e_campos` o que a pessoa pode FAZER ou PREENCHER nela.
 */
export interface ContextoDeTelaNaoAutoritativo {
  /**
   * Versão do envelope. Existe para o consumidor despachar sem adivinhar: o
   * runtime aceita a forma antiga e esta, e ignora o que não reconhece.
   */
  versao: '3.0.0'
  /** Marca a classe de confiança no próprio dado, para quem consumir sem ler o tipo. */
  natureza: 'nao_autoritativo'
  /** Rota corrente da tela observada. Snapshot sem rota legível não existe. */
  rota: string
  /**
   * Identificador da tela no vocabulário do produto, quando ela se publica.
   * `null` quando só há o DOM — aí a rota é a única identificação que existe.
   */
  tela: string | null
  /** O que a tela É: identidade, modo, período, abas, filtros, tabelas, colunas. */
  estrutura: ItemDeTela[]
  /** O que a tela MOSTRA: linhas, valores e totais à vista. */
  dados: ItemDeTela[]
  /** O que a pessoa pode FAZER ou PREENCHER: botões, links e controles. */
  acoes_e_campos: ItemDeTela[]
  /** Quantos itens visíveis a heurística de sensíveis reteve. */
  campos_omitidos_por_sensibilidade: number
  /** Quantos itens visíveis não couberam no cap. */
  campos_omitidos_por_limite: number
}

/**
 * As três listas de volta numa só, na ordem em que o prompt as apresenta.
 * Para quem precisa contar ou medir o snapshot inteiro — nunca para enviar.
 */
export function todosOsItensDoContexto (contexto: ContextoDeTelaNaoAutoritativo): ItemDeTela[] {
  return [...contexto.estrutura, ...contexto.dados, ...contexto.acoes_e_campos]
}

export interface EstiloObservado {
  display?: string
  visibility?: string
}

/**
 * Superfície da janela hospedeira usada pela leitura e pelos testes de DOM.
 */
export interface JanelaObservavel extends JanelaDaTela {
  document?: Document | null
  getComputedStyle?: (elemento: Element) => EstiloObservado | null
}

export interface OpcoesDoObservadorDeTela {
  /** Seletor de raízes cujas subárvores inteiras não pertencem à tela descrita. */
  ignorar?: string
  /**
   * Contexto publicado pela tela. Omitir busca o provedor registrado; passar
   * `null` observa só o DOM. Existe para o teste não depender de estado global.
   */
  publicado?: ContextoPublicadoPelaTela | null
}

export interface IdentificacaoDeCampo {
  tipo?: string | null
  nome?: string | null
  id?: string | null
  rotulo?: string | null
  placeholder?: string | null
  autocomplete?: string | null
}

/** Minúsculas, sem acento, camelCase e separadores quebrados: `["senha","atual"]`. */
function tokenizar (partes: Array<string | null | undefined>): string[] {
  return partes
    .filter((parte): parte is string => typeof parte === 'string' && parte.length > 0)
    .flatMap(parte => parte
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .normalize('NFD')
      .replace(MARCAS_DIACRITICAS, '')
      .toLowerCase()
      .split(/[^a-z0-9]+/))
    .filter(token => token.length > 0)
}

/**
 * O campo é sensível? Regra explícita e conservadora, aplicada sobre tipo,
 * nome, id, rótulo, placeholder e autocomplete.
 *
 * Excluído: `type="password"` sempre; qualquer token que comece por um radical
 * de senha/segredo/documento pessoal; qualquer par sensível co-ocorrente.
 *
 * Deixa passar DE PROPÓSITO (vocabulário do produto que o Assistente precisa
 * enxergar): "cartão de ponto", "chave do registro", CNPJ, e-mail, telefone,
 * matrícula, salário, dados bancários, data de nascimento. São dados de negócio
 * das telas de gestão — retê-los transformaria a peneira em parede e o
 * observador ficaria cego justamente nas telas que ele existe para orientar.
 */
export function ehCampoSensivel (campo: IdentificacaoDeCampo): boolean {
  if ((campo.tipo ?? '').toLowerCase() === 'password') return true

  const tokens = tokenizar([campo.nome, campo.id, campo.rotulo, campo.placeholder, campo.autocomplete])
  if (tokens.length === 0) return false

  const casa = (radical: string): boolean => tokens.some(token => token.startsWith(radical))
  if (RADICAIS_SENSIVEIS.some(casa)) return true

  return PARES_SENSIVEIS.some(([um, outro]) => casa(um) && casa(outro))
}

function estiloDe (elemento: Element, janela: JanelaObservavel): EstiloObservado | null {
  try {
    const computado = janela.getComputedStyle?.(elemento)
    if (computado) return computado
  } catch {
    // Estilo ilegível não decide visibilidade: cai no `style` inline abaixo.
  }

  const inline = (elemento as HTMLElement).style
  return inline ? { display: inline.display, visibility: inline.visibility } : null
}

export function tipoDoCampo (elemento: Element): string {
  const tag = elemento.tagName.toLowerCase()
  if (tag === 'textarea') return 'textarea'
  if (tag === 'select') {
    return (elemento as HTMLSelectElement).multiple ? 'select-multiple' : 'select'
  }
  return (elemento.getAttribute('type') || 'text').toLowerCase()
}

/**
 * O campo está visível para a PESSOA? O que ela não vê, o agente não vê.
 *
 * Sem depender de layout: fora do DOM
 * renderizado, `type="hidden"`, ou — em qualquer ancestral — `hidden`,
 * `aria-hidden="true"`, `display: none` ou `visibility: hidden`.
 */
export function ehCampoVisivel (elemento: Element, janela: JanelaObservavel): boolean {
  if (elemento.isConnected === false) return false
  if (tipoDoCampo(elemento) === 'hidden') return false

  let atual: Element | null = elemento
  while (atual) {
    if (atual.hasAttribute('hidden')) return false
    if (atual.getAttribute('aria-hidden') === 'true') return false

    const estilo = estiloDe(atual, janela)
    if (estilo?.display === 'none') return false
    if (estilo?.visibility === 'hidden' || estilo?.visibility === 'collapse') return false

    atual = atual.parentElement
  }

  return true
}

/**
 * O asterisco de campo obrigatório, no fim do rótulo.
 *
 * O design system desenha `<label>nome*</label>` e `obrigatório` já é estado
 * do campo, não parte do nome dele. Sem tirar, a barra de preenchimento dizia
 * "Preenchi nome da regra * com …" — marca de formulário na cara da pessoa.
 */
const MARCA_DE_OBRIGATORIO = /\s*\*+\s*$/

function normalizarEspacos (texto: string): string {
  return texto.replace(/\s+/g, ' ').trim().replace(MARCA_DE_OBRIGATORIO, '').trim()
}

/**
 * Texto de rótulo de uma subárvore: o que a pessoa LÊ, sem o conteúdo dos
 * controles que moram lá dentro. É o que faz `<label>Horário <input><select>…`
 * render "Horário" para os dois campos, em vez de arrastar as opções do select.
 */
function textoDeRotulo (no: Node): string {
  if (no.nodeType === 3) return no.nodeValue ?? ''
  if (no.nodeType !== 1) return ''

  const elemento = no as Element
  if (TAGS_SEM_TEXTO_DE_ROTULO.has(elemento.tagName.toLowerCase())) return ''

  return Array.from(elemento.childNodes).map(textoDeRotulo).join(' ')
}

/**
 * O rótulo que a tela mostra mas o HTML não associa.
 *
 * As telas do produto (design system Imaginii) renderizam `<div class=
 * "p-textfield"><input><label for="textfield">Nome da escala</label></div>`:
 * `for` aponta para um id que não existe e o input não tem `name` nem `id`.
 * Nenhuma associação padrão resolve — e a pessoa, olhando, lê "Nome da escala".
 *
 * A regra que fecha essa lacuna sem tocar em tela nenhuma: subir até quatro
 * níveis enquanto o contêiner tiver EXATAMENTE UM controle, e usar o `<label>`
 * de lá (ou, na falta dele, o texto do contêiner). O "exatamente um" é o que
 * separa contexto de chute: um contêiner com dois campos não sabe de qual é o
 * texto, e emprestá-lo daria a TODOS o rótulo do primeiro — foi o que o teste
 * contra a tela viva mostrou. Quando a regra não fecha, o campo entra
 * `sem_rotulo`: registro, nunca invenção.
 */
function rotuloDoContainer (
  elemento: Element,
  extrairTexto: (no: Node) => string = textoDeRotulo
): { rotulo: string, origem: OrigemDoRotulo } | null {
  let container = elemento.parentElement
  let nivel = 0

  while (container && nivel < MAX_NIVEIS_DE_CONTAINER) {
    if (container.querySelectorAll(SELETOR_DE_CAMPOS).length !== 1) return null

    const etiqueta = container.querySelector('label')
    if (etiqueta) {
      const texto = normalizarEspacos(extrairTexto(etiqueta))
      if (texto.length > 0) return { rotulo: texto, origem: 'label-no-container' }
    }

    const texto = normalizarEspacos(extrairTexto(container))
    if (texto.length > 0) return { rotulo: texto, origem: 'texto-no-container' }

    container = container.parentElement
    nivel += 1
  }

  return null
}

/**
 * Índice `label[for]` → `<label>` do documento. Exportado porque a ESCRITA
 * (`preenchimentoDeTela.ts`) precisa achar o campo pelo mesmo rótulo que a
 * leitura descreve; duas heurísticas de rótulo divergentes seriam duas telas
 * diferentes para a mesma pessoa.
 */
export function mapaDeRotulosPorFor (documento: Document): Map<string, Element> {
  const rotulosPorFor = new Map<string, Element>()
  for (const rotulo of Array.from(documento.querySelectorAll('label[for]'))) {
    const alvo = rotulo.getAttribute('for')
    if (alvo && !rotulosPorFor.has(alvo)) rotulosPorFor.set(alvo, rotulo)
  }
  return rotulosPorFor
}

export function rotuloDoCampo (
  elemento: Element,
  documento: Document,
  rotulosPorFor: Map<string, Element>,
  extrairTexto: (no: Node) => string = textoDeRotulo
): { rotulo: string | null, origem: OrigemDoRotulo } {
  const referencias = (elemento.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean)
  if (referencias.length > 0) {
    const texto = normalizarEspacos(referencias
      .map(id => {
        const alvo = documento.getElementById(id)
        return alvo ? extrairTexto(alvo) : ''
      })
      .join(' '))
    if (texto.length > 0) return { rotulo: texto, origem: 'aria-labelledby' }
  }

  const ariaLabel = normalizarEspacos(elemento.getAttribute('aria-label') || '')
  if (ariaLabel.length > 0) return { rotulo: ariaLabel, origem: 'aria-label' }

  const id = elemento.getAttribute('id')
  const associado = id ? rotulosPorFor.get(id) : undefined
  if (associado) {
    const texto = normalizarEspacos(extrairTexto(associado))
    if (texto.length > 0) return { rotulo: texto, origem: 'label-for' }
  }

  const ancestral = elemento.closest('label')
  if (ancestral) {
    const texto = normalizarEspacos(extrairTexto(ancestral))
    if (texto.length > 0) return { rotulo: texto, origem: 'label-ancestral' }
  }

  const doContainer = rotuloDoContainer(elemento, extrairTexto)
  if (doContainer) return doContainer

  const placeholder = normalizarEspacos(elemento.getAttribute('placeholder') || '')
  if (placeholder.length > 0) return { rotulo: placeholder, origem: 'placeholder' }

  // Registro, não conserto: a tela não associa rótulo nenhum a este campo, e o
  // snapshot diz isso em vez de deixar o campo sumir em silêncio.
  return { rotulo: null, origem: 'sem_rotulo' }
}

function valorDoCampo (elemento: Element, tipo: string): { valor: string, preenchido: boolean } {
  if (tipo === 'checkbox' || tipo === 'radio') {
    const marcado = (elemento as HTMLInputElement).checked === true
    return { valor: marcado ? 'true' : 'false', preenchido: marcado }
  }

  const bruto = String((elemento as HTMLInputElement).value ?? '')

  // Caminho de arquivo nunca vira valor observado (o upload tem change própria);
  // só o fato de haver arquivo escolhido.
  if (tipo === 'file') return { valor: '', preenchido: bruto.length > 0 }

  if (tipo === 'select' || tipo === 'select-multiple') {
    if (bruto.length === 0) return { valor: '', preenchido: false }
    const escolhidas = Array.from((elemento as HTMLSelectElement).selectedOptions || [])
      .map(opcao => normalizarEspacos(opcao.textContent || ''))
      .filter(texto => texto.length > 0)
    return { valor: escolhidas.length > 0 ? escolhidas.join(', ') : bruto, preenchido: true }
  }

  return { valor: bruto, preenchido: bruto.trim().length > 0 }
}

interface ExclusoesDaObservacao {
  ignorar?: string
  paineisDeAbasInativas: Set<Element>
}

interface ResultadoDosCampos {
  campos: ItemDeTela[]
  filtros: ItemDeTela[]
  omitidosPorSensibilidade: number
}

interface ResultadoDasRegioes {
  regioes: Array<RegiaoComLinhas<ItemDeTela>>
  omitidosPorSensibilidade: number
}

function seletorEhValido (documento: Document, seletor: string | undefined): boolean {
  if (!seletor) return true
  try {
    documento.querySelector(seletor)
    return true
  } catch {
    return false
  }
}

function estaNaSubarvore (elemento: Element, raizes: Set<Element>): boolean {
  for (const raiz of raizes) {
    if (raiz === elemento || raiz.contains(elemento)) return true
  }
  return false
}

function estaIgnorado (elemento: Element, exclusoes: ExclusoesDaObservacao): boolean {
  if (exclusoes.ignorar && elemento.closest(exclusoes.ignorar)) return true
  return estaNaSubarvore(elemento, exclusoes.paineisDeAbasInativas)
}

function estaDisponivel (
  elemento: Element,
  janela: JanelaObservavel,
  exclusoes: ExclusoesDaObservacao
): boolean {
  return !estaIgnorado(elemento, exclusoes) && ehCampoVisivel(elemento, janela)
}

function criarExclusoes (
  documento: Document,
  ignorar: string | undefined
): ExclusoesDaObservacao {
  const paineisDeAbasInativas = new Set<Element>()
  const abas = Array.from(documento.querySelectorAll('[role="tab"]'))

  for (const aba of abas) {
    if (ignorar && aba.closest(ignorar)) continue
    if (aba.getAttribute('aria-selected') === 'true') continue

    const controles = (aba.getAttribute('aria-controls') || '').split(/\s+/).filter(Boolean)
    for (const id of controles) {
      const painel = documento.getElementById(id)
      if (painel) paineisDeAbasInativas.add(painel)
    }
  }

  for (const painel of Array.from(documento.querySelectorAll('[role="tabpanel"]'))) {
    const referencias = (painel.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean)
    const abasDonas = referencias
      .map(id => documento.getElementById(id))
      .filter((elemento): elemento is HTMLElement => elemento?.getAttribute('role') === 'tab')

    if (abasDonas.length > 0 && abasDonas.every(aba => aba.getAttribute('aria-selected') !== 'true')) {
      paineisDeAbasInativas.add(painel)
    }
  }

  return { ignorar, paineisDeAbasInativas }
}

function textoObservavel (
  no: Node,
  janela: JanelaObservavel,
  exclusoes: ExclusoesDaObservacao
): string {
  if (no.nodeType === 3) return no.nodeValue ?? ''
  if (no.nodeType !== 1) return ''

  const elemento = no as Element
  if (!estaDisponivel(elemento, janela, exclusoes)) return ''
  return Array.from(elemento.childNodes)
    .map(filho => textoObservavel(filho, janela, exclusoes))
    .join(' ')
}

function textoDeRotuloObservavel (
  no: Node,
  janela: JanelaObservavel,
  exclusoes: ExclusoesDaObservacao
): string {
  if (no.nodeType === 3) return no.nodeValue ?? ''
  if (no.nodeType !== 1) return ''

  const elemento = no as Element
  if (!estaDisponivel(elemento, janela, exclusoes)) return ''
  if (TAGS_SEM_TEXTO_DE_ROTULO.has(elemento.tagName.toLowerCase())) return ''
  return Array.from(elemento.childNodes)
    .map(filho => textoDeRotuloObservavel(filho, janela, exclusoes))
    .join(' ')
}

function rotuloAcessivel (
  elemento: Element,
  documento: Document,
  janela: JanelaObservavel,
  exclusoes: ExclusoesDaObservacao
): { rotulo: string | null, origem: OrigemDoRotulo } {
  const referencias = (elemento.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean)
  if (referencias.length > 0) {
    const texto = normalizarEspacos(referencias
      .map(id => {
        const referencia = documento.getElementById(id)
        return referencia ? textoObservavel(referencia, janela, exclusoes) : ''
      })
      .join(' '))
    if (texto.length > 0) return { rotulo: texto, origem: 'aria-labelledby' }
  }

  const ariaLabel = normalizarEspacos(elemento.getAttribute('aria-label') || '')
  if (ariaLabel.length > 0) return { rotulo: ariaLabel, origem: 'aria-label' }

  if (elemento.tagName.toLowerCase() === 'table') {
    const legenda = (elemento as HTMLTableElement).caption
    const texto = legenda ? normalizarEspacos(textoObservavel(legenda, janela, exclusoes)) : ''
    if (texto.length > 0) return { rotulo: texto, origem: 'texto-no-container' }
  }

  const titulo = normalizarEspacos(elemento.getAttribute('title') || '')
  if (titulo.length > 0) return { rotulo: titulo, origem: 'texto-no-container' }

  return { rotulo: null, origem: 'sem_rotulo' }
}

function criarItemSintetico (
  tipo: TipoDeItemDeTela,
  rotulo: string | null,
  origem: OrigemDoRotulo,
  valor: string
): CampoObservado {
  return {
    nome: null,
    rotulo,
    origem_do_rotulo: origem,
    tipo,
    valor,
    preenchido: valor.trim().length > 0
  }
}

function ehControleDeFiltro (elemento: Element): boolean {
  if (elemento.matches('input[type="search"], [role="searchbox"]')) return true
  return elemento.closest(SELETOR_DE_ESCOPO_DE_FILTROS) !== null
}

function descreverCampos (
  documento: Document,
  janela: JanelaObservavel,
  exclusoes: ExclusoesDaObservacao
): ResultadoDosCampos {
  const rotulosPorFor = mapaDeRotulosPorFor(documento)
  const extrairTexto = (no: Node): string => textoDeRotuloObservavel(no, janela, exclusoes)
  const campos: CampoObservado[] = []
  const filtros: CampoObservado[] = []
  let omitidosPorSensibilidade = 0

  for (const elemento of Array.from(documento.querySelectorAll(SELETOR_DE_CAMPOS))) {
    const tipo = tipoDoCampo(elemento)
    if (TIPOS_DE_ACAO.has(tipo)) continue
    if (!estaDisponivel(elemento, janela, exclusoes)) continue

    const { rotulo, origem } = rotuloDoCampo(elemento, documento, rotulosPorFor, extrairTexto)
    const nome = elemento.getAttribute('name') || elemento.getAttribute('id') || null
    const identificacao = {
      tipo,
      nome,
      id: elemento.getAttribute('id'),
      rotulo,
      placeholder: elemento.getAttribute('placeholder'),
      autocomplete: elemento.getAttribute('autocomplete')
    }

    if (ehCampoSensivel(identificacao)) {
      omitidosPorSensibilidade += 1
      continue
    }

    const { valor, preenchido } = valorDoCampo(elemento, tipo)
    campos.push({
      nome,
      rotulo,
      origem_do_rotulo: origem,
      tipo,
      valor,
      preenchido
    })

    if (preenchido && ehControleDeFiltro(elemento)) {
      filtros.push(criarItemSintetico('estado_filtro', rotulo, origem, valor))
    }
  }

  return { campos, filtros, omitidosPorSensibilidade }
}

/**
 * Nome acessível de um controle de ação: o que a pessoa leria em voz alta.
 * Ordem: `aria-label`/`aria-labelledby`, texto visível, `title`, `value`.
 */
function rotuloDaAcao (
  elemento: Element,
  documento: Document,
  janela: JanelaObservavel,
  exclusoes: ExclusoesDaObservacao
): { rotulo: string | null, origem: OrigemDoRotulo } {
  const acessivel = rotuloAcessivel(elemento, documento, janela, exclusoes)
  if (acessivel.rotulo !== null) return acessivel

  const texto = normalizarEspacos(textoObservavel(elemento, janela, exclusoes))
  if (texto.length > 0) return { rotulo: texto, origem: 'texto-no-container' }

  const valor = normalizarEspacos(elemento.getAttribute('value') || '')
  if (valor.length > 0) return { rotulo: valor, origem: 'texto-no-container' }

  return { rotulo: null, origem: 'sem_rotulo' }
}

function estaDesabilitado (elemento: Element): boolean {
  if (elemento.hasAttribute('disabled')) return true
  return elemento.getAttribute('aria-disabled') === 'true'
}

/**
 * O que a pessoa pode acionar na tela, em ordem de documento.
 *
 * Ação sem nome nenhum não entra: um item "sem rótulo: " no prompt é ruído com
 * aparência de informação. Ação desabilitada ENTRA, marcada — saber que o
 * botão existe e está bloqueado é metade da orientação que a pessoa pede.
 */
function descreverAcoes (
  documento: Document,
  janela: JanelaObservavel,
  exclusoes: ExclusoesDaObservacao
): ItemDeTela[] {
  const acoes: ItemDeTela[] = []
  const vistos = new Set<string>()

  for (const elemento of Array.from(documento.querySelectorAll(SELETOR_DE_ACOES))) {
    if (!estaDisponivel(elemento, janela, exclusoes)) continue
    // A aba ATIVA já viaja como `estado_aba`: repeti-la como ação diria que
    // dá para "clicar para ir" onde a pessoa já está. A aba inativa continua
    // sendo ação — ir para ela é algo que dá para fazer.
    if (elemento.getAttribute('role') === 'tab' && elemento.getAttribute('aria-selected') === 'true') continue
    if (elemento.tagName.toLowerCase() === 'a') {
      const destino = (elemento.getAttribute('href') || '').trim()
      if (HREFS_SEM_ACAO.has(destino)) continue
    }

    const { rotulo, origem } = rotuloDaAcao(elemento, documento, janela, exclusoes)
    if (rotulo === null) continue

    const chave = rotulo.toLowerCase()
    if (vistos.has(chave)) continue
    vistos.add(chave)

    const item: ItemDeTela = {
      nome: elemento.getAttribute('name') || elemento.getAttribute('id') || null,
      rotulo,
      origem_do_rotulo: origem,
      tipo: 'acao',
      valor: rotulo,
      preenchido: false
    }
    if (estaDesabilitado(elemento)) item.desabilitado = true
    acoes.push(item)
    if (acoes.length >= LIMITE_DE_ACOES_OBSERVADAS) break
  }

  return acoes
}

function descreverAbas (
  documento: Document,
  janela: JanelaObservavel,
  exclusoes: ExclusoesDaObservacao
): CampoObservado[] {
  const estados: CampoObservado[] = []

  for (const aba of Array.from(documento.querySelectorAll('[role="tab"][aria-selected="true"]'))) {
    if (!estaDisponivel(aba, janela, exclusoes)) continue

    const conjunto = aba.closest('[role="tablist"]')
    if (conjunto && !estaDisponivel(conjunto, janela, exclusoes)) continue

    const rotuloDoConjunto = conjunto
      ? rotuloAcessivel(conjunto, documento, janela, exclusoes)
      : { rotulo: null, origem: 'sem_rotulo' as OrigemDoRotulo }
    const rotuloDaAba = rotuloAcessivel(aba, documento, janela, exclusoes).rotulo
    const textoDaAba = rotuloDaAba ?? normalizarEspacos(textoObservavel(aba, janela, exclusoes))
    if (textoDaAba.length === 0) continue

    estados.push(criarItemSintetico(
      'estado_aba',
      rotuloDoConjunto.rotulo,
      rotuloDoConjunto.origem,
      textoDaAba
    ))
  }

  return estados
}

function linhasDaRegiao (regiao: Element): Element[] {
  const ehLista = ['ul', 'ol'].includes(regiao.tagName.toLowerCase()) || regiao.getAttribute('role') === 'list'
  const seletor = ehLista ? 'li, [role="listitem"]' : 'tr, [role="row"]'

  return Array.from(regiao.querySelectorAll(seletor))
    .filter(linha => linha.closest(SELETOR_DE_REGIOES) === regiao)
}

function celulasDaLinha (linha: Element): Element[] {
  if (linha.matches('li, [role="listitem"]')) return [linha]

  return Array.from(linha.querySelectorAll(
    'th, td, [role="cell"], [role="gridcell"], [role="rowheader"], [role="columnheader"]'
  )).filter(celula => celula.closest('tr, [role="row"]') === linha)
}

function ehLinhaDeCabecalho (linha: Element, celulas: Element[]): boolean {
  if (linha.closest('thead')) return true
  if (celulas.length === 0) return false
  return celulas.every(celula => {
    const papel = celula.getAttribute('role')
    return celula.tagName.toLowerCase() === 'th' || papel === 'columnheader'
  })
}

/**
 * Título que a pessoa associa à tabela quando o HTML não associa nada.
 *
 * Sobe até três níveis procurando um cabeçalho (`h1`-`h6` ou `[role=heading]`)
 * que venha ANTES da região no mesmo contêiner. É o padrão do produto:
 * `<section><h2>Empregados</h2><table>…`, sem `caption` nem `aria-label`.
 */
function tituloVizinhoDaRegiao (
  regiao: Element,
  janela: JanelaObservavel,
  exclusoes: ExclusoesDaObservacao
): string | null {
  let container: Element | null = regiao.parentElement
  let nivel = 0

  while (container && nivel < 3) {
    const cabecalhos = Array.from(container.querySelectorAll('h1, h2, h3, h4, h5, h6, [role="heading"]'))
      .filter(cabecalho => estaDisponivel(cabecalho, janela, exclusoes))
      .filter(cabecalho => (cabecalho.compareDocumentPosition(regiao) & 4) !== 0)

    const ultimo = cabecalhos[cabecalhos.length - 1]
    if (ultimo) {
      const texto = normalizarEspacos(textoObservavel(ultimo, janela, exclusoes))
      if (texto.length > 0) return texto
    }

    container = container.parentElement
    nivel += 1
  }

  return null
}

function descreverRegiao (
  regiao: Element,
  documento: Document,
  janela: JanelaObservavel,
  exclusoes: ExclusoesDaObservacao,
  ordem: number
): { grupo: RegiaoComLinhas<ItemDeTela>, omitidosPorSensibilidade: number } | null {
  if (!estaDisponivel(regiao, janela, exclusoes)) return null

  let identificacao = rotuloAcessivel(regiao, documento, janela, exclusoes)
  if (identificacao.rotulo === null) {
    // Tabela sem rótulo era tabela DESCARTADA, e com ela sumiam todas as
    // linhas: o modelo recebia uma tela de listagem sem a listagem. Agora ela
    // entra identificada pelo título vizinho — e, na falta dele, pela ordem em
    // que a pessoa a vê, que é registro honesto e não invenção de nome.
    const vizinho = tituloVizinhoDaRegiao(regiao, janela, exclusoes)
    identificacao = vizinho !== null
      ? { rotulo: vizinho, origem: 'texto-no-container' }
      : { rotulo: `tabela ${ordem + 1}`, origem: 'sem_rotulo' }
  }

  const candidatas = linhasDaRegiao(regiao)
    .filter(linha => estaDisponivel(linha, janela, exclusoes))
  const cabecalho = candidatas.find(linha => {
    const celulas = celulasDaLinha(linha)
    return ehLinhaDeCabecalho(linha, celulas)
  })
  const celulasDoCabecalho = cabecalho ? celulasDaLinha(cabecalho) : []
  const indicesSensiveis = new Set<number>()
  const colunas: string[] = []

  celulasDoCabecalho.forEach((celula, indice) => {
    if (!estaDisponivel(celula, janela, exclusoes)) return
    const texto = normalizarEspacos(textoObservavel(celula, janela, exclusoes))
    if (texto.length === 0) return
    if (ehCampoSensivel({ tipo: 'text', rotulo: texto })) {
      indicesSensiveis.add(indice)
    } else {
      colunas.push(texto)
    }
  })

  const linhasVisiveis = candidatas.filter(linha => {
    const celulas = celulasDaLinha(linha)
    return !ehLinhaDeCabecalho(linha, celulas)
  })
  const linhas: CampoObservado[] = []
  let omitidosPorSensibilidade = 0

  for (const linha of linhasVisiveis) {
    const valores: string[] = []
    celulasDaLinha(linha).forEach((celula, indice) => {
      if (!estaDisponivel(celula, janela, exclusoes)) return

      const texto = normalizarEspacos(textoObservavel(celula, janela, exclusoes))
      if (indicesSensiveis.has(indice) || ehCampoSensivel({ tipo: 'text', rotulo: texto })) {
        omitidosPorSensibilidade += 1
        return
      }
      if (texto.length > 0) valores.push(texto)
    })

    linhas.push(criarItemSintetico(
      'regiao_linha',
      identificacao.rotulo,
      identificacao.origem,
      valores.join(' · ')
    ))
  }

  const resumo = [
    `${linhasVisiveis.length} linhas`,
    colunas.join(', ')
  ].filter(parte => parte.length > 0).join(' · ')

  return {
    grupo: {
      regiao: criarItemSintetico(
        'regiao_tabela',
        identificacao.rotulo,
        identificacao.origem,
        resumo
      ),
      linhas
    },
    omitidosPorSensibilidade
  }
}

function descreverRegioes (
  documento: Document,
  janela: JanelaObservavel,
  exclusoes: ExclusoesDaObservacao
): ResultadoDasRegioes {
  const regioes: Array<RegiaoComLinhas<ItemDeTela>> = []
  let omitidosPorSensibilidade = 0

  Array.from(documento.querySelectorAll(SELETOR_DE_REGIOES)).forEach((regiao, ordem) => {
    const descricao = descreverRegiao(regiao, documento, janela, exclusoes, ordem)
    if (!descricao) return
    regioes.push(descricao.grupo)
    omitidosPorSensibilidade += descricao.omitidosPorSensibilidade
  })

  return { regioes, omitidosPorSensibilidade }
}

/**
 * Item publicado pela tela, na forma do snapshot.
 *
 * `origem_do_rotulo` é `aria-label` por analogia deliberada: a tela ESCREVEU
 * aquele rótulo para ser lido, que é exatamente o crédito que a procedência
 * mede. Nada aqui foi adivinhado por vizinhança.
 */
function converterItemPublicado (item: ItemPublicadoPelaTela): ItemDeTela {
  const rotulo = item.rotulo === null || item.rotulo === undefined ? null : String(item.rotulo)
  const valor = String(item.valor ?? '')
  const convertido: ItemDeTela = {
    nome: item.nome ?? null,
    rotulo,
    origem_do_rotulo: rotulo === null ? 'sem_rotulo' : 'aria-label',
    tipo: item.tipo,
    valor,
    preenchido: valor.trim().length > 0
  }
  if (item.desabilitado === true) convertido.desabilitado = true
  return convertido
}

/**
 * Os itens de uma das três listas publicadas, já filtrados pelo vocabulário:
 * um item declarado com tipo de outra lista vai para a lista a que o tipo
 * pertence, e não para a que a tela escreveu. O vocabulário manda.
 */
function itensPublicados (
  publicado: ContextoPublicadoPelaTela | null,
  lista: 'estrutura' | 'dados' | 'acoes'
): ItemDeTela[] {
  if (publicado === null) return []
  const declarados = publicado[lista] ?? []
  if (!Array.isArray(declarados)) return []

  return declarados
    .filter(item => item && typeof item.tipo === 'string')
    .filter(item => {
      if (lista === 'estrutura') return ehTipoDeEstrutura(item.tipo)
      if (lista === 'dados') return ehTipoDeDados(item.tipo)
      return !ehTipoDeEstrutura(item.tipo) && !ehTipoDeDados(item.tipo)
    })
    .map(converterItemPublicado)
}

export function observarTela (
  janela: JanelaObservavel | null | undefined,
  opcoes: OpcoesDoObservadorDeTela = {}
): ContextoDeTelaNaoAutoritativo | null {
  let documento: Document | null | undefined
  try {
    documento = janela?.document
  } catch {
    return null
  }
  if (!documento || typeof documento.querySelectorAll !== 'function') return null
  if (!seletorEhValido(documento, opcoes.ignorar)) return null

  const janelaSegura = janela as JanelaObservavel
  const rota = lerRotaDaJanela(janela)
  if (rota === null) return null

  const exclusoes = criarExclusoes(documento, opcoes.ignorar)
  const resultadoDosCampos = descreverCampos(documento, janelaSegura, exclusoes)
  const resultadoDasRegioes = descreverRegioes(documento, janelaSegura, exclusoes)
  const estadosDeAba = descreverAbas(documento, janelaSegura, exclusoes)
  const acoes = descreverAcoes(documento, janelaSegura, exclusoes)
  const publicado = opcoes.publicado === undefined
    ? colherContextoPublicadoPelaTela()
    : opcoes.publicado

  // O que a tela publicou vem PRIMEIRO em cada lista: ela sabe o que o DOM não
  // tem como contar (o mês inteiro por trás do scroller virtual, o modo em que
  // está, o que aquela linha significa). O DOM entra depois, completando.
  // Abas e filtros LIDOS DO DOM entram com teto próprio: numa tela de busca,
  // cada campo preenchido vira um `estado_filtro`, e quarenta deles empurravam
  // para fora da lista justamente o cabeçalho das tabelas — a tela perdia a
  // listagem para descrever o próprio formulário duas vezes.
  const estadosObservados = [...estadosDeAba, ...resultadoDosCampos.filtros]
  const estrutura = [
    ...itensPublicados(publicado, 'estrutura'),
    ...estadosObservados.slice(0, LIMITE_DE_ESTADOS)
  ]
  const orcamento = preencherOrcamentoDoSnapshot({
    estrutura,
    campos: resultadoDosCampos.campos,
    acoes: [...itensPublicados(publicado, 'acoes'), ...acoes],
    dados: itensPublicados(publicado, 'dados'),
    regioes: resultadoDasRegioes.regioes
  })

  return {
    versao: '3.0.0',
    natureza: 'nao_autoritativo',
    rota,
    tela: publicado?.tela ?? null,
    estrutura: orcamento.estrutura,
    dados: orcamento.dados,
    acoes_e_campos: orcamento.acoes_e_campos,
    campos_omitidos_por_sensibilidade:
      resultadoDosCampos.omitidosPorSensibilidade + resultadoDasRegioes.omitidosPorSensibilidade,
    campos_omitidos_por_limite: orcamento.omitidosPorLimite
  }
}

export type ColheitaDeContextoDeTela = () => ContextoDeTelaNaoAutoritativo | null

/**
 * O dock registra a leitura da tela hospedeira ao montar e a desfaz ao
 * desmontar. A store usa o callback para colher o contexto de cada turno.
 */
let colheitaDaTelaAberta: ColheitaDeContextoDeTela | null = null

export function registrarColheitaDeContextoDeTela (colheita: ColheitaDeContextoDeTela | null): void {
  colheitaDaTelaAberta = colheita
}

/**
 * Falha ou ausência da leitura retorna `null` e permite enviar o turno.
 */
export function colherContextoDaTelaAberta (): ContextoDeTelaNaoAutoritativo | null {
  if (colheitaDaTelaAberta === null) return null
  try {
    return colheitaDaTelaAberta()
  } catch {
    return null
  }
}
